/**
 * Firebase Authentication Service
 * Handles Firebase initialization and authentication operations
 */

import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';

let firebaseApp = null;
let firebaseAuth = null;
let authInitialized = false;

/**
 * Firebase config — public values, safe to bundle in client code.
 * Real security comes from Firebase Authorized Domains, Security Rules,
 * and backend token verification. Removing the runtime fetch from the backend
 * means login works even if api.conso.getdashmote.com is down.
 */
const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyDHgysfUSg_q-zanDl9O4BEyvSoyIIiBZc',
  authDomain: 'ggm-app-b62b6.firebaseapp.com',
  projectId: 'ggm-app-b62b6',
  storageBucket: 'ggm-app-b62b6.firebasestorage.app',
  messagingSenderId: '1008058101305',
  appId: '1:1008058101305:web:d7e1b54e1eb417db8aa7e8',
};

/**
 * Initialize Firebase
 */
export async function initFirebase() {
  if (firebaseApp && firebaseAuth) {
    return { app: firebaseApp, auth: firebaseAuth };
  }

  try {
    // Initialize Firebase if not already initialized
    if (getApps().length === 0) {
      firebaseApp = initializeApp(FIREBASE_CONFIG);
    } else {
      firebaseApp = getApps()[0];
    }

    firebaseAuth = getAuth(firebaseApp);
    authInitialized = true;

    return { app: firebaseApp, auth: firebaseAuth };
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    throw error;
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(email, password) {
  try {
    // Validate email domain before attempting login
    validateEmailDomain(email);
    
    const { auth } = await initFirebase();
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Double-check email domain after login (in case email was changed)
    if (!isDashmoteEmail(userCredential.user.email)) {
      // Sign out immediately if email is not from allowed domain
      await firebaseSignOut(auth);
      throw new Error(`Only @dashmote.com email addresses are allowed. Your email: ${userCredential.user.email}`);
    }
    
    return userCredential.user;
  } catch (error) {
    console.error('Sign in error:', error);
    // If error is already our domain validation error, throw it as-is
    if (error.message && error.message.includes('@dashmote.com')) {
      throw error;
    }
    throw error;
  }
}

/**
 * Sign up with email and password
 */
export async function signUp(email, password) {
  try {
    // Validate email domain before attempting registration
    validateEmailDomain(email);
    
    const { auth } = await initFirebase();
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Double-check email domain after registration (in case email was changed)
    if (!isDashmoteEmail(userCredential.user.email)) {
      // Sign out immediately if email is not from allowed domain
      await firebaseSignOut(auth);
      throw new Error(`Only @dashmote.com email addresses are allowed. Your email: ${userCredential.user.email}`);
    }
    
    return userCredential.user;
  } catch (error) {
    console.error('Sign up error:', error);
    // If error is already our domain validation error, throw it as-is
    if (error.message && error.message.includes('@dashmote.com')) {
      throw error;
    }
    throw error;
  }
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle() {
  try {
    const { auth } = await initFirebase();
    const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
    
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    
    const userCredential = await signInWithPopup(auth, provider);
    
    // Validate email domain after Google login
    if (!isDashmoteEmail(userCredential.user.email)) {
      // Sign out immediately if email is not from allowed domain
      await firebaseSignOut(auth);
      throw new Error(`Only @dashmote.com email addresses are allowed. Your email: ${userCredential.user.email}`);
    }
    
    return userCredential.user;
  } catch (error) {
    console.error('Google sign in error:', error);
    
    // If error is already our domain validation error, throw it as-is
    if (error.message && error.message.includes('@dashmote.com')) {
      throw error;
    }
    
    // Handle specific Google auth errors
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in popup was closed. Please try again.');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Popup was blocked by browser. Please allow popups for this site.');
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      throw new Error('An account already exists with this email. Please sign in with your password.');
    }
    
    throw error;
  }
}

/**
 * Sign out
 */
export async function signOut() {
  try {
    const { auth } = await initFirebase();
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

/**
 * Get current user
 */
export function getCurrentUser() {
  if (!firebaseAuth) {
    return null;
  }
  return firebaseAuth.currentUser;
}

/**
 * Get ID token
 */
export async function getIdToken() {
  try {
    const { auth } = await initFirebase();
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
    return null;
  } catch (error) {
    console.error('Get ID token error:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return getCurrentUser() !== null;
}

/**
 * Allowed email domain for authentication
 */
const ALLOWED_EMAIL_DOMAIN = '@dashmote.com';

/**
 * Check if email is from dashmote.com domain
 * @param {string} email - Email address
 * @returns {boolean} True if email is from dashmote.com
 */
export function isDashmoteEmail(email) {
  if (!email) return false;
  return email.toLowerCase().endsWith(ALLOWED_EMAIL_DOMAIN);
}

/**
 * Validate email domain (must be @dashmote.com)
 * @param {string} email - Email address to validate
 * @throws {Error} If email is not from dashmote.com domain
 */
export function validateEmailDomain(email) {
  if (!email) {
    throw new Error('Email address is required');
  }
  
  if (!isDashmoteEmail(email)) {
    throw new Error(`Only @dashmote.com email addresses are allowed. Your email: ${email}`);
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(callback) {
  // Wrap callback to validate email domain
  const wrappedCallback = (user) => {
    if (user && user.email) {
      if (!isDashmoteEmail(user.email)) {
        console.warn('[Auth] Blocking login for non-dashmote.com email:', user.email);
        // Sign out immediately if email is not from allowed domain
        if (firebaseAuth) {
          firebaseSignOut(firebaseAuth).catch((error) => {
            console.error('Failed to sign out invalid user:', error);
          });
        }
        // Call callback with null to indicate user is not authenticated
        callback(null);
        return;
      }
    }
    callback(user);
  };

  if (!firebaseAuth) {
    // If Firebase is not initialized, initialize it first
    let unsubscribe = null;
    initFirebase().then(({ auth }) => {
      unsubscribe = onAuthStateChanged(auth, wrappedCallback);
    });
    // Return a function that will unsubscribe when called
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  } else {
    // Firebase is already initialized, return unsubscribe function directly
    return onAuthStateChanged(firebaseAuth, wrappedCallback);
  }
}

